import { add, listen, qs, qsa, remove } from "@fluorescent/dom";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import { createFocusTrap } from "focus-trap";
import { disableLinks, enableLinks } from "@/lib/a11y";
import { animateDrawerMenu } from "@/lib/animation";

import Search from "@/lib/search";

const sel = {
  overlay: "[data-overlay]",
  listItem: "[data-list-item]",
  item: "[data-item]",
  allLinks: "[data-all-links]",
  main: "[data-main]",
  primary: "[data-primary-container]",
  footer: "[data-footer]",
  searchSettings: "[data-search-settings]",
  link: ".drawer-menu__link",
  subMenuLinks: ".drawer-menu__item > .drawer-menu__link",
  topLevelLinks: '[data-depth="0"] > .drawer-menu__item > .drawer-menu__link',

  // Cross border
  form: ".drawer-menu__form",
  localeInput: "[data-locale-input]",
  currencyInput: "[data-currency-input]",
};

const classes = {
  active: "active",
  visible: "visible",
  childVisible: "child-visible",
  countrySelector: "drawer-menu__list--country-selector",
};

// Extra space we add to the height of the inner container
const formatHeight = h => h + 8 + "px";

const menu = node => {
  const focusTrap = createFocusTrap(node, {
    initialFocus: ".drawer-menu__link",
  });
  const { theme } = window;

  // Entire links container
  let primaryDepth = 0;
  // The individual link list the merchant selected
  let linksDepth = 0;

  // Element that holds all links, primary and secondary
  const everything = qs(sel.allLinks, node);

  // This is the element that holds the one we move left and right (primary)
  // We also need to assign its height initially so we get smooth transitions
  const main = qs(sel.main, node);

  // Element that holds all the primary links and moves left and right
  const primary = qs(sel.primary, node);

  // Cross border
  const form = qs(sel.form, node);
  const localeInput = qs(sel.localeInput, node);
  const currencyInput = qs(sel.currencyInput, node);

  const settings = qs(sel.searchSettings, node);
  const search = Search(node, {
    ...JSON.parse(settings.innerHTML),
    strings: {
      articles: theme.strings.search.headings.articles,
      pages: theme.strings.search.headings.pages,
      products: theme.strings.search.headings.products,
      view_all: theme.strings.search.view_all,
    },
  });

  // Nodes
  const overlay = qs(sel.overlay, node);
  const parents = qsa('[data-item="parent"]', node);
  const parentBack = qsa('[data-item="back"]', node);
  const secondary = qsa('[data-item="secondary"]', node);
  const secondaryBack = qsa('[data-item="secondaryHeading"]', node);
  const languages = qsa('[data-item="locale"]', node);
  const currencies = qsa('[data-item="currency"]', node);

  const events = [
    // Click on overlay
    listen(overlay, "click", close),

    // Esc pressed
    listen(node, "keydown", ({ keyCode }) => {
      if (keyCode === 27) close();
    }),

    // Element that will navigate to child navigation list
    listen(parents, "click", clickParent),

    // Element that will navigate back up the tree
    listen(parentBack, "click", clickBack),

    // Account, currency, and language link at the bottom
    listen(secondary, "click", handleSecondaryLink),

    // Back link within 'Currency' or 'Language'
    listen(secondaryBack, "click", handleBackFromSecondaryMenu),

    // Individual language
    listen(languages, "click", e => handleCrossBorder(e, localeInput)),

    // Individual currency
    listen(currencies, "click", e => handleCrossBorder(e, currencyInput)),
  ];

  const drawerAnimations = animateDrawerMenu(node);

  function open() {
    add(node, classes.active);

    add(node, classes.visible);
    focusTrap.activate();

    disableBodyScroll(node, {
      allowTouchMove: el => {
        while (el && el !== document.body) {
          if (el.getAttribute("data-scroll-lock-ignore") !== null) {
            return true;
          }
          el = el.parentNode;
        }
      },
      reserveScrollBarGap: true,
    });

    if (primaryDepth === 0 && linksDepth === 0) {
      main.style.height = formatHeight(primary.offsetHeight);
      drawerAnimations.openMain(everything);
    }
  }

  function close() {
    focusTrap.deactivate();
    remove(node, classes.visible);
    setTimeout(() => {
      remove(node, classes.active);
      enableBodyScroll(node);
    }, 350);
  }

  function clickParent(e) {
    e.preventDefault();
    const link = e.currentTarget;
    const childMenu = link.nextElementSibling;
    const firstFocusable = qs(".drawer-menu__link", childMenu);

    setTimeout(() => {
      firstFocusable.focus();
    }, 250);

    add(childMenu, classes.visible);
    add(link.parentNode, classes.childVisible);

    main.style.height = formatHeight(childMenu.offsetHeight);

    const elements = qsa(`:scope > ${sel.listItem}`, childMenu);

    let links = qsa(`:scope > ${sel.subMenuLinks}`, childMenu);

    drawerAnimations.openSub(elements[0].parentNode);

    navigate((linksDepth += 1));

    disableAllLinksExcept(links, true);
    link.setAttribute("aria-expanded", true);
    childMenu.setAttribute("aria-hidden", false);
  }

  function disableAllLinksExcept(links, addFooterLinks) {
    // We also want to always enable footer items as they are always visible
    const toDisable = qsa(`${sel.link}`, node);

    let linksToEnable = links;

    if (addFooterLinks) {
      linksToEnable.push(...qsa(`${sel.footer} ${sel.link}`, node));
    }

    disableLinks(toDisable);
    enableLinks(linksToEnable);
  }

  function navigate(depth) {
    linksDepth = depth;
    primary.setAttribute("data-depth", depth);
  }

  function navigatePrimary(depth) {
    primaryDepth = depth;
    everything.setAttribute("data-depth", depth);
  }

  function clickBack(e) {
    e.preventDefault();

    const menuBefore = e.currentTarget.closest(sel.listItem).closest("ul");
    const firstFocusable = qs(".drawer-menu__link", menuBefore);
    const menu = e.currentTarget.closest("ul");
    const parentLink = qs(".drawer-menu__link", menu.parentNode);
    const menuBeforeLinks = qsa(`:scope > ${sel.subMenuLinks}`, menuBefore);

    main.style.height = formatHeight(menuBefore.offsetHeight);

    remove(menu, classes.visible);
    remove(parentLink.parentNode, classes.childVisible);
    navigate((linksDepth -= 1));

    parentLink.setAttribute("aria-expanded", false);
    menu.setAttribute("aria-hidden", true);

    disableAllLinksExcept(menuBeforeLinks, true);

    setTimeout(() => {
      firstFocusable.focus();
    }, 250);
  }

  function handleSecondaryLink(e) {
    e.preventDefault();
    navigatePrimary(1);
    const childMenu = e.currentTarget.nextElementSibling;
    add(childMenu, classes.visible);

    const elements = qsa(sel.link, childMenu);

    // let links = qsa(`:scope > ${sel.subMenuLinks}`, childMenu);

    disableAllLinksExcept(elements);

    if (!childMenu.classList.contains(classes.countrySelector)) {
      drawerAnimations.openSub(elements[0].parentNode);
    }

    // const firstFocusable = qs('.drawer-menu__link', childMenu);

    setTimeout(() => {
      elements[0].focus();
    }, 250);
  }

  function handleBackFromSecondaryMenu(e) {
    e.preventDefault();
    navigatePrimary(0);

    const topLeveLinks = qsa(`${sel.topLevelLinks}`, node);

    disableAllLinksExcept(topLeveLinks, true);
    const parent = e.currentTarget.closest("ul");
    remove(parent, classes.visible);

    setTimeout(() => {
      topLeveLinks[0].focus();
    }, 250);
  }

  function handleCrossBorder(e, input) {
    const { value } = e.currentTarget.dataset;
    input.value = value;
    close();
    form.submit();
  }

  function destroy() {
    events.forEach(unsubscribe => unsubscribe());
    enableBodyScroll(node);
    search.destroy();
  }

  return { close, destroy, open };
};

export default menu;
